import React, { useState, useEffect, useRef } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import HC_more from "highcharts/highcharts-more";
import exporting from "highcharts/modules/exporting";
import mouseWheelZoom from "highcharts/modules/mouse-wheel-zoom";

// Initialize Highcharts modules
HC_more(Highcharts);
exporting(Highcharts);
mouseWheelZoom(Highcharts);

/**
 * LivePlantCombineChart component displays a Highcharts chart with a column series and a line series.
 * The Y-axis scales for both series are synchronized.
 *
 * @param {Object} props - The component props.
 * @param {Array<Object>} props.barChartData - Data for the bar chart series. Expected to have a plant_capacity.
 * @param {Array<Object>} props.lineChartData - Data for the line chart series. Expected to have timestamp and MW.
 * @param {Object} props.colorCode - Object mapping selectedPlantId to a color code.
 * @param {string} props.selectedPlantId - The ID of the currently selected plant, used for bar chart color.
 */
const LivePlantCombineChart = ({ barChartData, lineChartData, colorCode, selectedPlantId,unit }) => {
  // State to hold the chart data (categories, bar data, line data)
  const [chartData, setChartData] = useState({
    categories: [], // Timestamps for X-axis
    barData: [], // Bar chart data (plant capacity)
    lineData: [], // Line chart data (MW values)
  });

  // Ref to access the Highcharts chart instance
  const chartRef = useRef(null);

  /**
   * Effect to process incoming lineChartData and barChartData.
   * Updates the chartData state, appending new unique timestamps and their corresponding
   * bar and line values.
   */
  useEffect(() => {
    // Only proceed if lineChartData is available and not empty
    if (lineChartData && lineChartData.length > 0) {
      const plantData = lineChartData[0]; // Assuming lineChartData contains an array with a single plant object

      // If lineChartData starts with a single data point, reset the chart to avoid duplicates
      if (plantData.data.length === 1) {
        setChartData({
          categories: [],
          barData: [],
          lineData: [],
        });
      }

      // Extract and format new timestamps from the incoming data
      const newTimestamps = plantData.data.map((item) =>
        new Date(item.timestamp).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      );

      // Extract line chart values (MW)
      const newLineValues = plantData.data.map((item) => item.MW || 0);

      // Update the chartData state
      setChartData((prevData) => {
        // Filter out timestamps that are already in the categories array to ensure uniqueness
        const uniqueTimestamps = newTimestamps.filter(
          (timestamp) => !prevData.categories.includes(timestamp)
        );

        // If no new unique timestamps, return the previous state to avoid unnecessary re-renders
        if (uniqueTimestamps.length === 0) {
          return prevData;
        }

        // Return the new state by appending unique timestamps, bar data, and line data
        return {
          categories: [...prevData.categories, ...uniqueTimestamps], // Append unique timestamps
          barData: [
            ...prevData.barData,
            // Map each unique timestamp to the plant capacity from barChartData
            ...uniqueTimestamps.map(() => barChartData[0]?.plant_capacity || 0),
          ],
          lineData: [
            ...prevData.lineData,
            // Append the line values corresponding to the unique timestamps
            ...newLineValues.slice(-uniqueTimestamps.length),
          ],
        };
      });
    }
  }, [lineChartData, barChartData]); // Dependencies for this effect

  /**
   * Effect to scroll the chart to the most recent data points whenever chartData changes.
   */
  useEffect(() => {
    const chart = chartRef.current?.chart; // Get the Highcharts chart instance
    if (chart) {
      // Scroll the X-axis to show the last 10 data points
      chart.xAxis[0].setExtremes(
        Math.max(0, chartData.categories.length - 10), // Start from index (length - 10) or 0
        chartData.categories.length - 1 // End at the last index
      );
      chart.redraw(); // Redraw the chart to apply the new extremes
    }
  }, [chartData]); // Dependency for this effect

  /**
   * Helper function to dynamically calculate the min, max, and tick interval for the Y-axis.
   * This ensures the Y-axis scales adapt to the data range.
   *
   * @param {Array<number>} data - The data array (e.g., chartData.lineData) to calculate the range from.
   * @returns {Object} An object containing min, max, and tickInterval for the Y-axis.
   */
  const calculateYAxisRange = (data) => {
    // If no data, return a default range
    if (data.length === 0) return { min: 0, max: 100, tickInterval: 10 };

    const min = Math.min(...data); // Calculate the minimum value in the data
    const max = Math.max(...data); // Calculate the maximum value in the data
    const range = max - min; // Calculate the range of the data

    // Set a dynamic tick interval, ensuring at least 5 ticks
    const tickInterval = Math.ceil(range / 5) || 1; // Ensure tickInterval is at least 1

    // Return rounded min, max, and the calculated tick interval
    return {
      min: Math.floor(min / tickInterval) * tickInterval, // Round down to the nearest tick
      max: Math.ceil(max / tickInterval) * tickInterval, // Round up to the nearest tick
      tickInterval: tickInterval,
    };
  };

  // Calculate the Y-axis range based on the line chart data
  const yAxisRange = calculateYAxisRange(chartData.lineData);

  // Highcharts options configuration
  const options = {
    chart: {
      zooming: { mouseWheel: { enabled: true, type: "x" } },
      type: "column", // Base chart type
      height: 300, // Fixed height for the chart
      zoomType: 'x', // Enable zooming on the x-axis
    },
    title: {
      text: null, // No chart title
    },
    xAxis: {
      categories: chartData.categories, // Set X-axis categories (timestamps)
      title: {
        text: null, // No X-axis title
      },
      crosshair: true, // Show crosshair on hover
    },
    exporting: {
      enabled: false, // Disable exporting
    },
    yAxis: [
      {
        // Primary Y-axis (left side) for the column series
        title: { text: "MW (Capacity DC)" },
        gridLineDashStyle: "Dash",
        gridLineWidth: 1,
        gridLineColor: "#e0e0e0",
        lineWidth: 1,
        lineColor: "#e0e0e0",
        opposite: false, // Position on the left side
        min: yAxisRange.min, // Set min based on calculated range
        max: yAxisRange.max, // Set max based on calculated range
        tickInterval: yAxisRange.tickInterval, // Set tick interval based on calculated range
        labels: {
          enabled: true,
          style: {
            fontSize: "10px",
            color: "#333",
          },
        },
      },
      {
        // Secondary Y-axis (right side) for the line series
        title: { text: "MW (Capacity DC)" },
        gridLineDashStyle: "Dash",
        gridLineWidth: 1,
        gridLineColor: "#e0e0e0",
        lineWidth: 1,
        lineColor: "#e0e0e0",
        opposite: true, // Position on the right side
        min: yAxisRange.min, // Set min based on calculated range (same as left)
        max: yAxisRange.max, // Set max based on calculated range (same as left)
        tickInterval: yAxisRange.tickInterval, // Set tick interval based on calculated range (same as left)
        labels: {
          enabled: true,
          style: {
            fontSize: "10px",
            color: "#333",
          },
        },
      },
    ],
    tooltip: {
      enabled: true,
    },
    // tooltip: {
    //   shared: true, // Show a shared tooltip for all series at the same point
    //   useHTML: true, // Allow HTML formatting in tooltip
    //   formatter: function () {
    //     // Custom formatter function for the tooltip content
    //     if (!this.points || !this.points.length) {
    //       return `<b>${this.x}</b><br/>No data available`;
    //     }

    //     let tooltipContent = `<b>${this.x}</b><br/>`;
    //     // Iterate through points to build tooltip content
    //     this.points.forEach((point) => {
    //       // Exclude "MWh" series from the first line of the tooltip to show relevant data
    //       if (point && point.series.name !== "MWh") {
    //         tooltipContent += `
    //           <span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${this.points[1]?.y.toFixed(2)}</b><br/>
    //         `;
    //       }
    //     });
    //     return tooltipContent;
    //   },
    // },
    credits: {
      enabled: false, // Hide the Highcharts logo
    },
    legend: {
      enabled: false, // Hide the legend
    },
    scrollbar: {
      enabled: true, // Enable the scrollbar for zooming
    },
    series: [
      {
        name: "MW", // Name for the column series
        type: "column", // Series type
        data: chartData.barData, // Data for the columns
        color: colorCode[selectedPlantId], // Color based on selected plant
        yAxis: 0, // Bind to the primary (left) Y-axis
      },
      {
        name: unit, // Name for the line series
        type: "line", // Series type
        data: chartData.lineData, // Data for the line
        color: "#45748b", // Fixed color for the line
        marker: {
          enabled: true, // Show markers on the line
        },
        lineWidth: 2, // Line thickness
        yAxis: 1, // Bind to the secondary (right) Y-axis
      },
    ],
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef} // Attach ref to HighchartsReact component
      />
    </div>
  );
};

export default LivePlantCombineChart;